"use client";

import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type {
  CreatePostingRequest,
  EmployerApplicantsQuery,
  UpdatePostingRequest,
} from "@chotca/config";
import { getSdk } from "../sdk";
import { qk } from "../query-keys";

export function useMyPostings() {
  return useQuery({
    queryKey: qk.postings.mine(),
    queryFn: async () => getSdk().postings.listMine(),
    staleTime: 30_000,
  });
}

export function useOrgPostings(orgId: string | null | undefined) {
  return useQuery({
    queryKey: qk.postings.byOrg(orgId ?? ""),
    queryFn: async () => getSdk().postings.listByOrg(orgId!),
    enabled: !!orgId,
  });
}

export function usePosting(postingId: string | null | undefined) {
  return useQuery({
    queryKey: qk.postings.detail(postingId ?? ""),
    queryFn: async () => getSdk().postings.getById(postingId!),
    enabled: !!postingId,
  });
}

export function usePostingEditConstraints(
  postingId: string | null | undefined,
) {
  return useQuery({
    queryKey: qk.postings.editConstraints(postingId ?? ""),
    queryFn: async () => getSdk().postings.getEditConstraints(postingId!),
    enabled: !!postingId,
    staleTime: 15_000,
  });
}

export function useApplicants(postingId: string | null | undefined) {
  return useQuery({
    queryKey: qk.applications.byPosting(postingId ?? ""),
    queryFn: async () => getSdk().postings.listApplicants(postingId!),
    enabled: !!postingId,
  });
}

export function useApplicantProfile(applicationId: string | null | undefined) {
  return useQuery({
    queryKey: qk.applications.profile(applicationId ?? ""),
    queryFn: async () => getSdk().applications.getProfile(applicationId!),
    enabled: !!applicationId,
    staleTime: 60_000,
  });
}

/** Global employer applicant list ("Danh sách ứng viên"). */
export function useEmployerApplicants(query: EmployerApplicantsQuery = {}) {
  return useQuery({
    queryKey: qk.applications.employer(query),
    queryFn: async () => getSdk().applications.listForEmployer(query),
    placeholderData: keepPreviousData,
  });
}

/** Interview-credit history (+/-) for the "Lượt phỏng vấn" card. */
export function useCreditLedger(limit = 20) {
  return useQuery({
    queryKey: qk.applications.creditLedger,
    queryFn: async () => getSdk().applications.creditLedger(limit),
    staleTime: 15_000,
  });
}

/** Tab badge counts for the employer applicant list. */
export function useEmployerApplicantCounts(orgAddressId?: string) {
  return useQuery({
    queryKey: qk.applications.employerCounts(orgAddressId),
    queryFn: async () => getSdk().applications.applicantCounts(orgAddressId),
    staleTime: 15_000,
  });
}

/** Bulk-reject applicants from the "Mới" tab. */
export function useRejectApplicantsBatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (applicationIds: string[]) =>
      getSdk().applications.rejectBatch(applicationIds),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.applications.employer() });
      qc.invalidateQueries({ queryKey: qk.applications.employerCounts() });
      qc.invalidateQueries({ queryKey: qk.applications.pending });
    },
  });
}

export function useCreatePosting(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreatePostingRequest) =>
      getSdk().postings.createForOrg(orgId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.postings.mine() });
      qc.invalidateQueries({ queryKey: qk.postings.byOrg(orgId) });
      // Open-posting count drives the quota card + "at limit" gate.
      qc.invalidateQueries({ queryKey: qk.orgs.insights(orgId) });
      // Publishing the first posting can raise verifyLevel (SCRUM-74) — refresh
      // the org so the verification card reflects the new level.
      qc.invalidateQueries({ queryKey: qk.orgs.detail(orgId) });
    },
  });
}

export function useUpdatePosting(postingId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: UpdatePostingRequest) =>
      getSdk().postings.update(postingId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.postings.detail(postingId) });
      qc.invalidateQueries({ queryKey: qk.postings.mine() });
    },
  });
}

export function useClosePosting(postingId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => getSdk().postings.close(postingId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.postings.detail(postingId) });
      qc.invalidateQueries({ queryKey: qk.postings.mine() });
      // Closing a posting frees a slot — refresh the open-posting quota. The
      // close hook only has postingId, so invalidate all orgs' insights (prefix
      // match); cheap and there's only ever one active employer org in session.
      qc.invalidateQueries({ queryKey: ["orgs", "insights"] });
    },
  });
}
